import React, { useState } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  ThemeProvider,
  createTheme,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Badge,
  InputBase,
  alpha,
  Grid,
  Paper,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AccountBalanceWallet as WalletIcon,
  Category as TokenIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Person as PersonIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Search as SearchIcon,
  MonetizationOn as MonetizationOnIcon,
  LockOutlined,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
// Using the new Soteria logo
// import logoImage from "../assets/logo.svg";
import { isValidAddress } from "../services/walletService";
import { getOGPUPrice } from "../services/blockchainService";

// Create custom theme with dark mode
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#7E57C2", // Purple as primary color
    },
    secondary: {
      main: "#64B5F6", // Light blue as secondary color
    },
    background: {
      default: "#121212",
      paper: "#1E1E1E",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        },
      },
    },
  },
});

// Define drawer width
const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [ogpuPrice, setOgpuPrice] = useState<string>("0.352483");
  const location = useLocation();
  
  // Fetch OGPU price on component mount
  React.useEffect(() => {
    const fetchPrice = async () => {
      try {
        const price = await getOGPUPrice();
        setOgpuPrice(price.toFixed(4));
      } catch (error) {
        console.error("Error fetching OGPU price:", error);
      }
    };

    fetchPrice();
  }, []);

  // Handle drawer toggle for mobile
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Toggle dark/light mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Handle search submission
  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const query = searchQuery.trim();

      if (isValidAddress(query)) {
        navigate(`/wallet?address=${query}`);
      } else if (query.length > 0) {
        // If not a valid address, could implement token search or show error
        alert(
          "Please enter a valid OpenGPU wallet address. Addresses should start with 0x and be 42 characters long."
        );
      }
    }
  };

  // Navigation items
  const navItems = [
    { text: "Home", icon: <DashboardIcon />, path: "/", description: "Wallet & token analysis dashboard" },
    { text: "Wallet Analysis", icon: <WalletIcon />, path: "/wallet-analyzer", description: "Analyze any wallet on OGPU Chain" },
    { text: "Token Analysis", icon: <TokenIcon />, path: "/token-analyzer", description: "Comprehensive token analysis & metrics" },
    { text: "Liquidity Provider", icon: <LockOutlined />, path: "/liquidity-provider", description: "Secure liquidity locking system" },
    { text: "Soteria Token", icon: <MonetizationOnIcon />, path: "/soteria-token", description: "Learn about SoteriaScan's native token" },
  ];



  // Drawer content
  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar /> {/* This creates space below the AppBar */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: `linear-gradient(45deg, ${darkTheme.palette.primary.dark} 0%, ${darkTheme.palette.primary.main} 100%)`,
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        mx: 2,
        mb: 3
      }}>
        <Box
          component="img"
          src="/assets/token-icons/st_logo.png"
          alt="Soteria Token"
          sx={{ height: 40, width: 40, mr: 1, borderRadius: '50%', boxShadow: '0 0 10px rgba(255,255,255,0.3)' }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            e.currentTarget.src = "https://via.placeholder.com/40x40?text=Soteria";
          }}
        />
        <Box>
          <Typography variant="h6" noWrap component="div" color="#ffffff" fontWeight="bold">
            SoteriaScan
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)" sx={{ display: 'block', mt: -0.5 }}>
            Blockchain Analysis Tool for OpenGPU Network
          </Typography>
        </Box>
      </Box>

      <List sx={{ px: 1, flexGrow: 1 }}>
        {navItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem
              key={item.text}
              disablePadding
              component={Link}
              to={item.path}
              sx={{
                color: "text.primary",
                textDecoration: "none",
                mb: 0.5,
              }}
            >
              <ListItemButton
                selected={isSelected}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  transition: 'all 0.2s ease',
                  background: isSelected ? `linear-gradient(90deg, ${alpha(darkTheme.palette.primary.main, 0.15)} 0%, rgba(126, 87, 194, 0.01) 100%)` : 'transparent',
                  border: isSelected ? `1px solid ${alpha(darkTheme.palette.primary.main, 0.2)}` : '1px solid transparent',
                  '&:hover': {
                    background: isSelected 
                      ? `linear-gradient(90deg, ${alpha(darkTheme.palette.primary.main, 0.2)} 0%, rgba(126, 87, 194, 0.05) 100%)`
                      : alpha(darkTheme.palette.primary.main, 0.05)
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isSelected ? "primary.main" : "text.secondary",
                    minWidth: 42
                  }}
                >
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      bgcolor: isSelected ? alpha(darkTheme.palette.primary.main, 0.15) : 'transparent',
                      color: isSelected ? darkTheme.palette.primary.main : alpha('#ffffff', 0.5)
                    }}
                  >
                    {item.icon}
                  </Avatar>
                </ListItemIcon>
                <Box>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isSelected ? 600 : 400,
                      fontSize: "0.95rem",
                    }}
                    sx={{ m: 0 }}
                  />
                  {item.description && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block', 
                        color: alpha('#ffffff', 0.5),
                        mt: -0.3,
                        fontSize: '0.75rem'
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}
                </Box>
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      
      <Divider sx={{ my: 2, opacity: 0.1 }} />
      
      <Box sx={{ p: 2 }}>
        <Box sx={{ 
          p: 2, 
          background: `linear-gradient(135deg, #3a1c71 0%, #d76d77 50%, #ffaf7b 100%)`,
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          mb: 2,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            position: 'absolute', 
            top: -20, 
            right: -20, 
            opacity: 0.2,
            transform: 'rotate(15deg)',
            fontSize: '5rem'
          }}>
            <TokenIcon fontSize="inherit" />
          </Box>
          <Typography variant="subtitle1" fontWeight={600} color="#ffffff">
            Soteria Token
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mb: 1.5 }}>
            Current price: ${ogpuPrice || "0.42"}
          </Typography>
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Market Cap
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#ffffff' }}>
                  $24.5M
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  24h Change
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#4caf50' }}>
                  +5.2%
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          <Button
            variant="contained"
            fullWidth
            component={Link}
            to="/soteria-token"
            size="small"
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              color: '#ffffff', 
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Token Details
          </Button>
        </Box>
        
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ opacity: 0.7 }}
        >
          SoteriaScan v1.0
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ opacity: 0.7, mt: 1, fontFamily: 'monospace', cursor: 'pointer' }}
          onClick={() => window.open('https://ogpuscan.io/token/0xa40CaaF23532CeE1a4fD546A2cc02dbb81de9F7c', '_blank')}
        >
          Contract: 0xa40CaaF23532CeE1a4fD546A2cc02dbb81de9F7c
        </Typography>
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: "rgba(30, 30, 30, 0.8)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: "none" } }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo for mobile only */}
            {isMobile && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  component="img"
                  src="/assets/token-icons/st_logo.png"
                  alt="Soteria"
                  sx={{ height: 30, width: 30, mr: 1, borderRadius: '50%', boxShadow: '0 0 8px rgba(255,255,255,0.2)' }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/30x30?text=Soteria";
                  }}
                />
                <Typography variant="h6" component="div">
                  OGPU Analyzer
                </Typography>
              </Box>
            )}

            {/* Spacer to replace search bar */}
            <Box
              sx={{
                flexGrow: 1,
              }}
            />


            {/* Right Side Tools */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* Toggle Dark Mode */}
              <IconButton color="inherit" onClick={toggleDarkMode}>
                {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>



        {/* Side Navigation Drawer */}
        <Box
          component="nav"
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        >
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better mobile performance
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
              },
            }}
          >
            {drawer}
          </Drawer>

          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", md: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                borderRight: "1px solid rgba(255,255,255,0.05)",
                boxShadow: "0 0 25px rgba(0,0,0,0.1)",
                background: alpha(darkTheme.palette.background.paper, 0.8),
                backdropFilter: "blur(10px)",
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            minHeight: "100vh",
            background:
              "linear-gradient(135deg, rgba(18,18,18,1) 0%, rgba(26,26,26,1) 100%)",
            transition: 'all 0.3s ease',
          }}
        >
          <Toolbar /> {/* Spacer to push content below app bar */}
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Layout;
